import { Link } from 'react-router-dom';
import {
  UXP_CO_OPS_ID,
  COMMAND_CENTER_METRICS,
  ORDER_INTEL_DIMENSIONS,
  ORDER_WORKSPACE_FEATURES,
  CUSTOMER_INTEL_FEATURES,
  FULFILLMENT_FEATURES,
  SHIPPING_FEATURES,
  RETURNS_REFUNDS_FEATURES,
  COMMUNICATION_FEATURES,
  REVIEW_INTEL_FEATURES,
  RETENTION_PREDICTIONS,
  ADVISOR_RECOMMENDATIONS,
  SLA_METRICS,
  COLLABORATION_FEATURES,
  ANALYTICS_METRICS,
  AI_LEARNING_SOURCES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_ORDER_INTEL,
  DEMO_ORDERS,
  DEMO_CUSTOMERS,
  DEMO_FULFILLMENT,
  DEMO_RETURNS,
  DEMO_RETENTION,
  DEMO_ADVISOR,
  buildCustomerOpsDashboard,
} from '../../customerops/framework';
import {
  FulfillmentCommandCenterPanel,
  OrderIntelligencePanel,
  OrderWorkspacePanel,
  CustomerIntelligenceCard,
  FulfillmentOperationsPanel,
  ReturnsRefundsPanel,
  RetentionEnginePanel,
  AICustomerOpsAdvisorPanel,
} from '../../components/customerops';
import { Card, KpiCard } from '../../components/ui';

export function CustomerOperationsPage() {
  const dashboard = buildCustomerOpsDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_CO_OPS_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Customer Operations Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Customer Fulfillment, Orders, Returns & Customer Experience Operating System —
          unifies orders, fulfillment, logistics, communication, returns, refunds, and retention.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Order Intel Dimensions" value={dashboard.order_intel_dimensions.toString()} />
        <KpiCard label="Workspace Features" value={dashboard.workspace_features.toString()} />
        <KpiCard label="Retention Predictions" value={dashboard.retention_predictions.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Customer Fulfillment Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <FulfillmentCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Order Intelligence</h2>
        <p className="mt-1 text-sm text-slate-500">{ORDER_INTEL_DIMENSIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <OrderIntelligencePanel rows={DEMO_ORDER_INTEL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Order Workspace</h2>
        <p className="mt-1 text-sm text-slate-500">{ORDER_WORKSPACE_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <OrderWorkspacePanel orders={DEMO_ORDERS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Customer Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{CUSTOMER_INTEL_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CustomerIntelligenceCard customers={DEMO_CUSTOMERS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Fulfillment Operations</h2>
          <p className="mt-1 text-sm text-slate-500">{FULFILLMENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <FulfillmentOperationsPanel queue={DEMO_FULFILLMENT} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Shipping & Delivery</h2>
        <p className="mt-1 text-sm text-slate-500">{SHIPPING_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="space-y-2 text-sm">
            <p><span className="font-medium">On-time delivery:</span> 96.2% · Avg transit: 2.4 days</p>
            <p><span className="font-medium">Delay predictions:</span> 12 shipments at risk · proactive notices queued</p>
            <p className="text-xs text-slate-500">Carrier mix: BlueDart 42% · Delhivery 38% · Amazon MFN 20%</p>
          </div>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Returns & Refunds</h2>
        <p className="mt-1 text-sm text-slate-500">{RETURNS_REFUNDS_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ReturnsRefundsPanel items={DEMO_RETURNS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Customer Communication</h2>
          <p className="mt-1 text-sm text-slate-500">{COMMUNICATION_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">3 unread marketplace messages · 2 pending AI reply drafts</p>
              <p className="text-slate-600 dark:text-slate-400">Sentiment: 78% positive · 12% neutral · 10% negative</p>
            </div>
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Review Intelligence</h2>
          <p className="mt-1 text-sm text-slate-500">{REVIEW_INTEL_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">Avg rating: 4.4 · 12 new reviews this week</p>
              <p className="text-slate-600 dark:text-slate-400">Top keywords: "battery life", "comfort", "value for money"</p>
              <p className="text-xs text-warning-600">2 negative reviews flagged — AI response drafts ready</p>
            </div>
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Retention Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{RETENTION_PREDICTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <RetentionEnginePanel predictions={DEMO_RETENTION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Customer Operations Advisor</h2>
        <p className="mt-1 text-sm text-slate-500">{ADVISOR_RECOMMENDATIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AICustomerOpsAdvisorPanel recommendations={DEMO_ADVISOR} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">SLA Management</h2>
        <p className="mt-1 text-sm text-slate-500">{SLA_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 text-sm">
            {SLA_METRICS.map((m) => (
              <div key={m} className="rounded-lg border border-slate-200 p-2 text-center dark:border-slate-700">
                <p className="text-xs text-slate-500">{m}</p>
                <p className="mt-1 font-semibold text-success-600">98%+</p>
              </div>
            ))}
          </div>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{ANALYTICS_METRICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Learning</h2>
          <p className="mt-2 text-xs text-slate-500">{AI_LEARNING_SOURCES.join(' · ')}</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Accessibility</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ACCESSIBILITY_STANDARDS.map((s) => (
            <span key={s} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{s}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
